import {
  collection,
  deleteField,
  doc,
  getDoc,
  onSnapshot,
  query,
  runTransaction,
  serverTimestamp,
  writeBatch,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytesResumable } from "firebase/storage";
import { db, storage } from "../Context/firebaseConfig";
import {
  CAMPAIGN_STATUSES,
  toDate,
  validateCampaignInput,
  validateGroupedSubmissionInput,
  validateSubmissionInput,
} from "../utils/clippingCampaignDomain";

const CAMPAIGNS = "ClippingCampaigns";
const MEMBERSHIPS = "ClippingCampaignMemberships";
const SUBMISSIONS = "ClippingCampaignSubmissions";
const PAYOUTS = "ClippingCampaignPayouts";
const RESOURCES = "ClippingCampaignResources";

function withId(snapshot) {
  return { id: snapshot.id, ...snapshot.data() };
}

function sortNewest(items) {
  return [...items].sort((left, right) => {
    const leftDate = toDate(left.createdAt)?.getTime() || 0;
    const rightDate = toDate(right.createdAt)?.getTime() || 0;
    return rightDate - leftDate;
  });
}

function requireUser(user) {
  if (!user?.uid) throw new Error("Sign in to continue.");
}

export function uploadCampaignBanner({ user, file, onProgress }) {
  requireUser(user);
  if (!file?.type?.startsWith("image/")) throw new Error("Choose a JPG, PNG, or WebP image.");
  if (file.size > 8 * 1024 * 1024) throw new Error("Campaign banners must be 8 MB or smaller.");

  const extension = file.name.split(".").pop()?.toLowerCase() || "jpg";
  const bannerRef = ref(storage, `clippingCampaigns/${user.uid}/banners/${Date.now()}.${extension}`);
  const uploadTask = uploadBytesResumable(bannerRef, file, {
    contentType: file.type,
    customMetadata: { ownerId: user.uid, uploadedAt: new Date().toISOString() },
  });

  return new Promise((resolve, reject) => {
    uploadTask.on(
      "state_changed",
      (snapshot) => onProgress?.(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)),
      reject,
      async () => resolve(getDownloadURL(uploadTask.snapshot.ref))
    );
  });
}

export function uploadCampaignResource({ user, file, onProgress }) {
  requireUser(user);
  if (!file || file.size < 1) throw new Error("Choose a resource file to upload.");
  if (file.size > 2 * 1024 * 1024 * 1024) {
    throw new Error("Campaign resource files must be 2 GB or smaller.");
  }

  const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "-").slice(-160) || "resource";
  const resourceRef = ref(
    storage,
    `clippingCampaigns/${user.uid}/resources/${Date.now()}-${safeName}`
  );
  const uploadTask = uploadBytesResumable(resourceRef, file, {
    contentType: file.type || "application/octet-stream",
    customMetadata: {
      ownerId: user.uid,
      originalName: file.name,
      uploadedAt: new Date().toISOString(),
    },
  });

  return new Promise((resolve, reject) => {
    uploadTask.on(
      "state_changed",
      (snapshot) => onProgress?.(
        Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
      ),
      reject,
      async () => resolve(getDownloadURL(uploadTask.snapshot.ref))
    );
  });
}

export function uploadClipSource({ user, file, onProgress }) {
  requireUser(user);
  if (!file?.type?.startsWith("video/")) throw new Error("Choose a video file for this edit.");
  if (file.size > 1024 * 1024 * 1024) throw new Error("Source edits must be 1 GB or smaller.");

  const safeName = file.name.replace(/[^a-zA-Z0-9._-]+/g, "-").slice(-160) || "clip.mp4";
  const sourceRef = ref(
    storage,
    `clippingCampaigns/${user.uid}/submissions/${Date.now()}-${safeName}`
  );
  const uploadTask = uploadBytesResumable(sourceRef, file, {
    contentType: file.type,
    customMetadata: {
      ownerId: user.uid,
      originalName: file.name,
      uploadedAt: new Date().toISOString(),
    },
  });
  return new Promise((resolve, reject) => {
    uploadTask.on(
      "state_changed",
      (snapshot) => onProgress?.(
        Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
      ),
      reject,
      async () => resolve({
        url: await getDownloadURL(uploadTask.snapshot.ref),
        name: file.name,
      })
    );
  });
}

async function callCampaignEndpoint({ campaignId, user, action, method = "POST", body }) {
  requireUser(user);
  const endpoint = String(process.env.REACT_APP_ENDPOINT || "").replace(/\/$/, "");
  if (!endpoint) throw new Error("Campaign payment endpoint is not configured.");
  const token = await user.getIdToken();
  const actionPath = action ? `/${action}` : "";
  const response = await fetch(
    `${endpoint}/api/clipping-campaigns/${campaignId}${actionPath}`,
    {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: body == null ? undefined : JSON.stringify(body),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || "Campaign payment request failed.");
  return data;
}

export function createCampaignFundingCheckout({ campaignId, user }) {
  return callCampaignEndpoint({ campaignId, user, action: "checkout" });
}

export function confirmCampaignFundingCheckout({ campaignId, sessionId, user }) {
  return callCampaignEndpoint({
    campaignId,
    user,
    action: "confirm-checkout",
    body: { sessionId },
  });
}

export function refundUnusedCampaignBudget({ campaignId, user }) {
  return callCampaignEndpoint({ campaignId, user, action: "refund-unused" });
}

export function deleteUnpaidCampaign({ campaignId, user }) {
  return callCampaignEndpoint({ campaignId, user, action: "", method: "DELETE" });
}

export function closeOwnedCampaign({ campaignId, user }) {
  return callCampaignEndpoint({ campaignId, user, action: "close" });
}

export function finalizeOwnedCampaignClosure({ campaignId, user }) {
  return callCampaignEndpoint({ campaignId, user, action: "finalize-closure" });
}

async function callPayoutEndpoint({ user, payoutId, action, body }) {
  requireUser(user);
  const endpoint = String(process.env.REACT_APP_ENDPOINT || "").replace(/\/$/, "");
  if (!endpoint) throw new Error("Campaign payout endpoint is not configured.");
  const token = await user.getIdToken();
  const payoutPath = payoutId ? `/${payoutId}` : "";
  const response = await fetch(
    `${endpoint}/api/clipping-payouts${payoutPath}/${action}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: body == null ? undefined : JSON.stringify(body),
    }
  );
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || "Campaign payout request failed.");
  return data;
}

export function startCampaignPayoutOnboarding({ user }) {
  return callPayoutEndpoint({ user, action: "connect" });
}

export function withdrawCampaignPayout({ payoutId, user }) {
  return callPayoutEndpoint({ user, payoutId, action: "withdraw" });
}

export function withdrawCampaignBalance({ payoutIds, user }) {
  return callPayoutEndpoint({
    user,
    action: "withdraw-balance",
    body: { payoutIds },
  });
}

async function callClippingApi({ user, path, method = "GET", body }) {
  const endpoint = String(process.env.REACT_APP_ENDPOINT || "").replace(/\/$/, "");
  if (!endpoint) throw new Error("Campaign endpoint is not configured.");
  const headers = { "Content-Type": "application/json" };
  if (user) headers.Authorization = `Bearer ${await user.getIdToken()}`;
  const response = await fetch(`${endpoint}${path}`, {
    method,
    headers,
    body: body == null ? undefined : JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || "Clipping campaign request failed.");
  return data;
}

export async function listSocialAccounts({ user, scope = "mine", campaignId = "" }) {
  requireUser(user);
  const params = new URLSearchParams({ scope });
  if (campaignId) params.set("campaignId", campaignId);
  const data = await callClippingApi({
    user,
    path: `/api/clipping-social-accounts?${params.toString()}`,
  });
  return data.accounts || [];
}

export function requestSocialAccountVerification({ user, platform, profileUrl, handle }) {
  requireUser(user);
  return callClippingApi({
    user,
    path: "/api/clipping-social-accounts",
    method: "POST",
    body: { platform, profileUrl, handle },
  });
}

export function reviewSocialAccountVerification({ user, accountId, action, reason, campaignId = "" }) {
  requireUser(user);
  return callClippingApi({
    user,
    path: `/api/clipping-social-accounts/${encodeURIComponent(accountId)}/review`,
    method: "POST",
    body: { action, reason, campaignId },
  });
}

export async function listCampaignReviews({ campaignId }) {
  const data = await callClippingApi({
    path: `/api/clipping-campaigns/${encodeURIComponent(campaignId)}/reviews`,
  });
  return data.reviews || [];
}

export async function listExistingCampaignIds({ campaignIds = [] }) {
  const uniqueIds = [...new Set(campaignIds.map((id) => String(id || "").trim()).filter(Boolean))];
  if (uniqueIds.length === 0) return [];
  const results = await Promise.all(uniqueIds.map(async (campaignId) => {
    try {
      const snapshot = await getDoc(doc(db, CAMPAIGNS, campaignId));
      return snapshot.exists() ? campaignId : null;
    } catch {
      return null;
    }
  }));
  return results.filter(Boolean);
}

export function createCampaignReview({ campaignId, user, subjectId, rating, comment }) {
  requireUser(user);
  return callClippingApi({
    user,
    path: `/api/clipping-campaigns/${encodeURIComponent(campaignId)}/reviews`,
    method: "POST",
    body: { subjectId, rating, comment },
  });
}

export async function createClippingCampaign({ user, profile, input }) {
  requireUser(user);
  if (profile?.profileInfo?.role?.toLowerCase() !== "creator") {
    throw new Error("Only creator accounts can create clipping campaigns.");
  }

  const validation = validateCampaignInput(input);
  if (!validation.isValid) {
    const error = new Error("Check the highlighted campaign fields.");
    error.fieldErrors = validation.errors;
    throw error;
  }

  const { resources, ...campaignData } = validation.value;
  const campaignRef = doc(collection(db, CAMPAIGNS));
  const resourcesRef = doc(db, RESOURCES, campaignRef.id);
  const batch = writeBatch(db);

  batch.set(campaignRef, {
    ...campaignData,
    creatorId: user.uid,
    creatorUsername: profile?.profileInfo?.username || "creator",
    creatorProfilePicUrl: profile?.profileInfo?.profilePicUrl || "",
    resourceNames: resources.map((resource) => resource.name),
    status: CAMPAIGN_STATUSES.DRAFT,
    reviewStatus: null,
    fundingStatus: "unfunded",
    approvedSpendCents: 0,
    budgetRemainingCents: campaignData.budgetCents,
    reservedBudgetCents: 0,
    memberCount: 0,
    submissionCount: 0,
    reviewedSubmissionCount: 0,
    approvedSubmissionCount: 0,
    approvalRate: null,
    version: 1,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    publishedAt: null,
  });

  batch.set(resourcesRef, {
    campaignId: campaignRef.id,
    creatorId: user.uid,
    resources,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  await batch.commit();

  return campaignRef.id;
}

export async function getCampaignDraftForEdit({ campaignId, user }) {
  requireUser(user);
  if (!campaignId) throw new Error("Campaign not found.");

  const campaignSnapshot = await getDoc(doc(db, CAMPAIGNS, campaignId));
  if (!campaignSnapshot.exists()) throw new Error("Campaign not found.");

  const campaign = withId(campaignSnapshot);
  if (campaign.creatorId !== user.uid) throw new Error("Only the campaign creator can edit this draft.");
  if (campaign.status !== CAMPAIGN_STATUSES.DRAFT) throw new Error("Only draft campaigns can be edited.");
  if (campaign.fundingStatus === "paid_needs_attention") {
    throw new Error("This paid campaign needs administrator attention before it can be edited.");
  }

  const resourcesSnapshot = await getDoc(doc(db, RESOURCES, campaignId));
  return {
    campaign,
    resources: resourcesSnapshot.exists() ? resourcesSnapshot.data().resources || [] : [],
  };
}

export async function updateClippingCampaign({ campaignId, user, input }) {
  requireUser(user);
  if (!campaignId) throw new Error("Campaign not found.");

  const validation = validateCampaignInput(input);
  if (!validation.isValid) {
    const error = new Error("Check the highlighted campaign fields.");
    error.fieldErrors = validation.errors;
    throw error;
  }

  const { resources, ...campaignData } = validation.value;
  const campaignRef = doc(db, CAMPAIGNS, campaignId);
  const resourcesRef = doc(db, RESOURCES, campaignId);

  await runTransaction(db, async (transaction) => {
    const campaignSnapshot = await transaction.get(campaignRef);
    if (!campaignSnapshot.exists()) throw new Error("Campaign not found.");

    const campaign = campaignSnapshot.data();
    if (campaign.creatorId !== user.uid) throw new Error("Only the campaign creator can edit this draft.");
    if (campaign.status !== CAMPAIGN_STATUSES.DRAFT) throw new Error("Only draft campaigns can be edited.");
    if (campaign.fundingStatus === "paid_needs_attention") {
      throw new Error("This paid campaign needs administrator attention before it can be edited.");
    }

    const fundingUpdates = campaign.fundingStatus === "checkout_pending"
      ? {
          fundingStatus: "checkout_invalidated",
          checkoutSessionId: null,
          checkoutCampaignVersion: null,
          fundingBreakdown: null,
        }
      : {};

    transaction.update(campaignRef, {
      ...campaignData,
      ...fundingUpdates,
      maxPayoutPerSubmissionCents: deleteField(),
      maxSubmissionsPerClipper: deleteField(),
      resourceNames: resources.map((resource) => resource.name),
      budgetRemainingCents: Math.max(0, campaignData.budgetCents - (campaign.approvedSpendCents || 0)),
      reviewStatus: null,
      reviewRequestedAt: null,
      version: (campaign.version || 1) + 1,
      updatedAt: serverTimestamp(),
    });
    transaction.set(resourcesRef, {
      campaignId,
      creatorId: user.uid,
      resources,
      updatedAt: serverTimestamp(),
    }, { merge: true });
  });

  return campaignId;
}

export function subscribeCampaigns({ userId, role, onData, onError }) {
  const isCreator = role?.toLowerCase() === "creator";
  const campaignsQuery = isCreator && userId
    ? query(collection(db, CAMPAIGNS), where("creatorId", "==", userId))
    : query(collection(db, CAMPAIGNS), where("status", "==", CAMPAIGN_STATUSES.LIVE));

  return onSnapshot(
    campaignsQuery,
    (snapshot) => onData(sortNewest(snapshot.docs.map(withId))),
    onError
  );
}

export function subscribeOwnedCampaigns(userId, onData, onError) {
  if (!userId) {
    onData([]);
    return () => {};
  }

  return onSnapshot(
    query(collection(db, CAMPAIGNS), where("creatorId", "==", userId)),
    (snapshot) => onData(sortNewest(snapshot.docs.map(withId))),
    onError
  );
}

export function subscribeAllCampaigns(userId, onData, onError) {
  if (!userId) {
    onData([]);
    return () => {};
  }

  return onSnapshot(
    collection(db, CAMPAIGNS),
    (snapshot) => onData(sortNewest(snapshot.docs.map(withId))),
    onError
  );
}

export function subscribeCampaign(campaignId, onData, onError) {
  if (!campaignId) return () => {};
  return onSnapshot(
    doc(db, CAMPAIGNS, campaignId),
    (snapshot) => onData(snapshot.exists() ? withId(snapshot) : null),
    onError
  );
}

export function subscribeUserMemberships(userId, onData, onError) {
  if (!userId) {
    onData([]);
    return () => {};
  }

  return onSnapshot(
    query(collection(db, MEMBERSHIPS), where("clipperId", "==", userId)),
    (snapshot) => onData(sortNewest(snapshot.docs.map(withId))),
    onError
  );
}

export function subscribeJoinedCampaigns(campaignIds, onData, onError) {
  const ids = [...new Set((campaignIds || []).filter(Boolean))];
  if (ids.length === 0) {
    onData([]);
    return () => {};
  }

  const campaigns = new Map();
  const publish = () => onData(sortNewest([...campaigns.values()]));
  const unsubscribes = ids.map((campaignId) => onSnapshot(
    doc(db, CAMPAIGNS, campaignId),
    (snapshot) => {
      if (snapshot.exists()) campaigns.set(campaignId, withId(snapshot));
      else campaigns.delete(campaignId);
      publish();
    },
    onError
  ));

  return () => unsubscribes.forEach((unsubscribe) => unsubscribe());
}

export async function joinClippingCampaign({ campaignId, user, rulesAccepted = false }) {
  return callCampaignEndpoint({
    campaignId,
    user,
    action: "join",
    body: { rulesAccepted },
  });
}

export async function updateCampaignStatus({ campaignId, user, status }) {
  const allowedStatuses = Object.values(CAMPAIGN_STATUSES);
  if (!allowedStatuses.includes(status)) throw new Error("Choose a valid campaign status.");
  return callCampaignEndpoint({ campaignId, user, action: "status", body: { status } });
}

export function subscribeCampaignSubmissions({ campaignId, userId, scope = "clipper", onData, onError }) {
  if (!userId) {
    onData([]);
    return () => {};
  }

  const submissionsQuery = campaignId
    ? query(collection(db, SUBMISSIONS), where("campaignId", "==", campaignId))
    : scope === "owner"
      ? query(collection(db, SUBMISSIONS), where("creatorId", "==", userId))
      : query(collection(db, SUBMISSIONS), where("clipperId", "==", userId));

  return onSnapshot(
    submissionsQuery,
    (snapshot) => {
      const submissions = snapshot.docs.map(withId);
      onData(sortNewest(submissions));
    },
    onError
  );
}

export async function submitCampaignClip({ campaignId, user, input }) {
  requireUser(user);

  const validation = Array.isArray(input.posts)
    ? validateGroupedSubmissionInput(input)
    : validateSubmissionInput(input);
  if (!validation.isValid) {
    const error = new Error("Check the highlighted submission fields.");
    error.fieldErrors = validation.errors;
    throw error;
  }

  const result = await callCampaignEndpoint({
    campaignId,
    user,
    action: "submissions",
    body: validation.value,
  });
  return result.submissionId;
}

export async function reviewCampaignSubmission({ campaignId, submissionId, user, action, views = 0, reason = "" }) {
  return callCampaignEndpoint({
    campaignId,
    user,
    action: `submissions/${submissionId}/review`,
    body: { action, views, reason },
  });
}

export async function appealCampaignSubmission({ campaignId, submissionId, user, reason }) {
  return callCampaignEndpoint({
    campaignId,
    user,
    action: `submissions/${submissionId}/appeal`,
    body: { reason },
  });
}

export async function resolveCampaignSubmissionDispute({
  campaignId,
  submissionId,
  user,
  action,
  views = 0,
  reason = "",
}) {
  return callClippingApi({
    user,
    path: `/api/clipping-operations/campaigns/${encodeURIComponent(campaignId)}/submissions/${encodeURIComponent(submissionId)}/resolve`,
    method: "POST",
    body: { action, views, reason },
  });
}

export async function listCampaignOperationsQueue({ user }) {
  requireUser(user);
  return callClippingApi({ user, path: "/api/clipping-operations/queue" });
}

export async function endSubmissionTrackingForTest({ campaignId, submissionId, user }) {
  return callCampaignEndpoint({
    campaignId,
    user,
    action: `submissions/${submissionId}/end-tracking-test`,
    body: {},
  });
}

export function subscribePayouts({ userId, scope = "clipper", onData, onError }) {
  if (!userId) {
    onData([]);
    return () => {};
  }

  const ownerField = scope === "owner" ? "creatorId" : "clipperId";
  return onSnapshot(
    query(collection(db, PAYOUTS), where(ownerField, "==", userId)),
    (snapshot) => onData(sortNewest(snapshot.docs.map(withId))),
    onError
  );
}

export async function getCampaignMembership(campaignId, userId) {
  if (!campaignId || !userId) return null;
  const snapshot = await getDoc(doc(db, MEMBERSHIPS, `${campaignId}_${userId}`));
  return snapshot.exists() ? withId(snapshot) : null;
}

export async function getCampaignResources({ campaignId, userId, creatorId }) {
  if (!campaignId || !userId) return [];
  const isCreator = userId === creatorId;
  const membership = isCreator ? { status: "owner" } : await getCampaignMembership(campaignId, userId);
  if (!membership) throw new Error("Join the campaign to open its resources.");
  const snapshot = await getDoc(doc(db, RESOURCES, campaignId));
  return snapshot.exists() ? snapshot.data().resources || [] : [];
}
